import { Checkbox } from "antd";
import { CheckboxProps } from "antd/lib/checkbox";
import classnames from "classnames";
import i18n, { I18N_NS } from "Config/i18n";
import { debounce } from "lodash";
import * as React from "react";
import ReactDOM from "react-dom";
import {
  Column as _Column,
  defaultTableRowRenderer,
  Index,
  IndexRange,
  InfiniteLoader as _InfiniteLoader,
  SortDirectionType,
  Table,
  TableHeaderRowProps,
  TableProps,
  TableRowRenderer,
  InfiniteLoaderProps,
} from "react-virtualized";
import { ColumnProps } from "react-virtualized/dist/es/Table";
import { isEmpty } from "utils/isEmpty";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { dndRowRenderer } from "./components/dndRowRenderer";
import { CustomFilterRenderProps, ScrollableTableHeader } from "./components/ScrollableTableHeader";
import { SortableHeaderRow } from "./components/SortableHeaderRow";
import { TableCore } from "./components/TableCore";
import { waitFor } from "utils/waitFor";
import { isEqual } from "utils/isEqual";
const Column = _Column as unknown as React.FC<ColumnProps>;
const InfiniteLoader = _InfiniteLoader as unknown as React.FC<InfiniteLoaderProps>;
const BATCH_SIZE = 120;
const DEFAULT_COLUMN_RESIZE_MIN_WIDTH = 48;

type ScrollableSortableHeaderRowProps = Pick<TableHeaderRowProps, "className" | "columns" | "style"> & {
  columnDataKeys: string[];
  onColumnReorder: (newOrder: string[]) => void;
};

const ScrollableSortableHeaderRow: React.FC<ScrollableSortableHeaderRowProps> = ({
  className,
  columns,
  style,
  columnDataKeys,
  onColumnReorder,
}) => (
  <SortableHeaderRow
    className={className}
    columns={columns}
    style={style}
    columnDataKeys={columnDataKeys}
    onColumnReorder={onColumnReorder}
  />
);

function createSortableHeaderRowRenderer(columnDataKeys: string[], onColumnReorder: (newOrder: string[]) => void) {
  return function sortableHeaderRowRenderer(headerRowProps: TableHeaderRowProps) {
    return React.createElement(ScrollableSortableHeaderRow, {
      className: headerRowProps.className,
      columns: headerRowProps.columns,
      style: headerRowProps.style,
      columnDataKeys,
      onColumnReorder,
    });
  };
}

function compareValues(key: string) {
  return function (a: object, b: object) {
    if (!Object.prototype.hasOwnProperty.call(a, key)) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) {
        return 0;
      }
      // property doesn't exist on either object
      return -1;
    }

    const varA = typeof a[key] === "string" ? a[key].toLowerCase() : a[key];
    const varB = typeof b[key] === "string" ? b[key].toLowerCase() : b[key];

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return comparison;
  };
}

export interface FilterConfig {
  text: string | React.ReactNode;
  /* Value can be only STRING because Menu.key property of FiltersDropdown.tsx is always string */
  value: string;
  description?: string;
  hidden?: boolean;
}

export type FilterType = string[] | number[];

export interface ActiveFilters {
  [namespace: string]: FilterType;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FilterPredicate = (filterOption: any, value: any) => boolean;
type CompareFunction<T> = (a: T, b: T) => number;

export interface TableColumnProps<T = object> extends ColumnProps {
  dataKey: keyof T;
  label?: React.ReactNode;
  filters?: FilterConfig[];
  /* predicate function for each column filter */
  filterPredicate?: FilterPredicate;
  sorter?: CompareFunction<T>;
  /* used to change sort order on first click of row. When you want certain column to initially sort descending */
  initialSortOrder?: SortDirectionType;
  customFilterRender?: (renderProps: CustomFilterRenderProps) => React.ReactNode;
  /** When true, filter dropdown allows only one selected value at a time (see FiltersDropdown). */
  singleFilter?: boolean;
  /** Optional hook for E2E: applied to the column header root (sort + filter area). */
  headerDataTestId?: string;
  /** Optional hook for E2E: applied to the filter trigger (funnel icon) in the column header. */
  filterIconDataTestId?: string;
}

export interface ScrollableTableProps<T = object> {
  /* Unique identifier for each row */
  rowKey: keyof T;
  data: Array<T>;
  columns: TableColumnProps<T>[];
  /* Wether to stretch table in flex container or not */
  stretchHeight?: boolean;
  /* Set default sorting column */
  defaultSortBy?: keyof T;
  /* Set default sorting direction */
  defaultSortDirection?: SortDirectionType;
  /* Control selection of rows */
  selectedRowKeys?: string[] | number[];
  /* Callback executed when row selection was changed */
  onSelectRows?: (argument: { selectedRows: T[]; selectedRowKeys: T[keyof T][] }) => void;
  /* Wether to show loading spinner or not */
  loading?: boolean;
  onRowClick?: (params: { rowData: T; index: number; event: React.MouseEvent }) => void;
  onSort?: (sortBy: keyof T, sortOrder: SortDirectionType) => void;
  onFilter?: (filters: ActiveFilters) => void;
  sortBy?: keyof T;
  sortDirection?: SortDirectionType;
  getCheckboxProps?: (rowData: T) => CheckboxProps;
  /* use to omit checking of some specific rows */
  onCheckAllPredicate?: (rowData: T) => boolean;
  rowClassName?: string | ((rowData: T) => string);
  highlightedRowId?: T[keyof T] | number[] | string[];
  rowCount?: number;
  rowHeight?: number;
  virtualizedTableProps?: Partial<TableProps>;
  draggableRows?: boolean /* enable dnd feature */;
  /* set row data that will included in "onDrop" callback. Default - {id: [rowData.id]} */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  normalizeDropRowData?: (rowData: T) => any;
  /* callback that is called when item is dropped on another */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDrop?: (args: { dragRowData: any; dropRowData: any }) => void;
  /* to set "draggable" prop on row in depend of rowData */
  getDraggableFromRowData?: (rowData: T) => boolean;
  /* stands for showing that row can be used to drop dragged row on it */
  getDroppableFromRowData?: (rowData: T) => boolean;
  wrapperStyles?: React.CSSProperties;
  /* to control minimum width of filter dropdown */
  filterDropdownMinWidth?: number;
  disableSort?: boolean;
  smallFontSize?: boolean; // font-size: 10px;
  loadMore?: (params: { limit: number; offset: number }) => Promise<void>;
  resizeableHeaders?: boolean | string;
  /** When column has no `minWidth`, use this floor for resize + localStorage restore (default 48). */
  resizeMinWidth?: number;
  /* for custom request to select all records */
  selectAllRequest?: () => Promise<unknown>;
  ignoreResizeableHeaders?: string[];
  /* to always trigger the "selectAllRequest" no matter the batch size */
  alwaysCallSelectAll?: boolean;
  /* to use data from props instead of state */
  useDataFromProps?: boolean;
  /** When provided, column headers become draggable to reorder columns. Callback receives new order of dataKeys. */
  onColumnReorder?: (newOrder: string[]) => void;
}

export interface ScrollableTableState<T = object> {
  sortBy: keyof T;
  sortDirection: SortDirectionType;
  data: T[];
  filters: ActiveFilters;
  selectedRows: T[];
  selectedRowKeys: T[keyof T][];
  scrollToIndex: number;
  loadingMore: boolean;
  adjustedColumnWidthes: { [columnIndex: number]: number };
}

export class ScrollableTable<T = object> extends React.Component<ScrollableTableProps<T>, ScrollableTableState<T>> {
  static displayName = "ScrollableTable";
  tableRef: React.RefObject<Table>;
  // cache for predicate functions
  private predicates: { [namespace: string]: FilterPredicate } = {};
  // cache for sorting functions
  private sorters: { [namespace: string]: CompareFunction<T> } = {};
  private startIndex: number;
  private stopIndex: number;
  saveWidthesToLocalStorageDebounce: (adjustedColumnWidthes: { [columnIndex: number]: number }) => void;
  debounceHandleLoadMore: InfiniteLoaderProps["loadMoreRows"];

  constructor(props: ScrollableTableProps<T>) {
    super(props);

    this.state = {
      sortBy: props.sortBy || props.defaultSortBy,
      sortDirection: props.sortDirection || props.defaultSortDirection || "ASC",
      data: props.data || [],
      filters: {},
      selectedRows: [],
      selectedRowKeys: [],
      scrollToIndex: undefined,
      loadingMore: false,
      adjustedColumnWidthes: {},
    };
    this.saveWidthesToLocalStorageDebounce = debounce(this.saveWidthesToLocalStorage, 500);
    this.tableRef = React.createRef();
    this.debounceHandleLoadMore = debounce(this.handleLoadMore, 200);
  }

  componentDidMount() {
    this.setPredicateFunctions();
    this.setSorterFunctions();
    this.readSavedWidtesFromLocalStorage();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowId = (record: Array<unknown> | T[keyof T]) => {
    if (typeof record === "object") {
      return record[0];
    } else {
      return record;
    }
  };

  componentDidUpdate(prevProps: ScrollableTableProps<T>, _prevState: ScrollableTableState<T>) {
    if (this.props.highlightedRowId) {
      if (
        (typeof this.state.scrollToIndex !== "number" &&
          this.state.scrollToIndex &&
          this.props.data?.length &&
          this.getRowId(this.props.highlightedRowId)) ||
        this.getRowId(this.props.highlightedRowId) !== this.getRowId(prevProps.highlightedRowId)
      ) {
        this.handleSetHighlightedRow();
      }
    }

    /**
     * We compare the length of string from the props data array using JSON.stringify to update the data.
     * Thus, we exclude the case when an array comes with the same length as previous.
     * This method is 99.9% accurate.
     *
     * @param {number} prevProps.data.length - old data
     * @param {number} this.props.data - new data
     */
    // const prevPropsLength = JSON.stringify(prevProps.data).length;
    // const nextPropsLength = JSON.stringify(this.props.data).length;
    // // sort updated data after receiving new one
    // if (this.props.data.length !== prevProps.data.length || prevPropsLength !== nextPropsLength) {
    //   this.setState({ data: this.props.data }, () => {
    //     this.sort({ sortBy: this.state.sortBy as string, sortDirection: this.state.sortDirection });
    //   });
    // }

    /**
     * Now it's 100% accurate and much faster than JSON.stringify
     * Also do not use @param JSON.stringify for a large amount of data cause it's very slow
     */
    if (!isEqual(prevProps.data, this.props.data)) {
      this.setState({ data: this.props.data }, () => {
        this.sort({ sortBy: this.state.sortBy as string, sortDirection: this.state.sortDirection });
      });
    }

    // when selected rows are handled through props - handle their change in table state
    if (this.props.selectedRowKeys && this.props.selectedRowKeys.length !== prevProps.selectedRowKeys.length) {
      const { rowKey, data, onCheckAllPredicate } = this.props;
      const selectedRows = [];
      this.props.selectedRowKeys.forEach((key) => {
        // rowKey values are not guaranteed to be unique across row kinds (e.g. a folder and a file
        // can share an id), so prefer a row the consumer considers selectable.
        const selectable =
          typeof onCheckAllPredicate === "function"
            ? data.find((item) => item?.[rowKey] === key && onCheckAllPredicate(item))
            : undefined;
        selectedRows.push(selectable || data.find((item) => item?.[rowKey] === key));
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.setState({ selectedRowKeys: this.props.selectedRowKeys as any, selectedRows });
    }
  }

  handleSetHighlightedRow() {
    const index = this.data.findIndex(
      (item) => item?.[this.props.rowKey] === this.getRowId(this.props.highlightedRowId)
    );
    if (index !== -1) {
      this.setState({ scrollToIndex: index });
    } else {
      if (this.tableRef.current) {
        this.tableRef.current.scrollToRow(0);
      }
      this.setState({ scrollToIndex: undefined });
    }
  }

  // Get predicate function for filtering from props and set them into local cache to optimize performance
  setPredicateFunctions() {
    if (this.props.columns.some((item) => !!item.filters)) {
      const predicates = {};
      this.props.columns.forEach((column) => {
        const key = column.dataKey;
        predicates[key as string] = column.filterPredicate;
      });
      this.predicates = predicates;
    }
  }

  // Get sorter function for sorting from props and set them into local cache to optimize performance
  setSorterFunctions() {
    if (this.props.columns.some((item) => !!item.sorter)) {
      const sorters = {};
      this.props.columns.forEach((column) => {
        const key = column.dataKey;
        sorters[key as string] = column.sorter;
      });
      this.sorters = sorters;
    }
  }

  sort = ({ sortBy, sortDirection }: { sortBy: string; sortDirection: SortDirectionType }) => {
    if (this.props.disableSort) {
      return;
    }
    const clickedColumn = this.props.columns.find((item) => item.dataKey === sortBy);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (this.state.sortBy !== sortBy && (clickedColumn as TableColumnProps<any>).initialSortOrder === "DESC") {
      sortDirection = "DESC";
    }
    // Handle sorting from props
    if (
      typeof this.props.onSort === "function" &&
      (sortBy !== this.state.sortBy || sortDirection !== this.state.sortDirection)
    ) {
      this.setState({ sortBy: sortBy as keyof T, sortDirection });
      this.props.onSort(sortBy as keyof T, sortDirection);
      return;
    }
    // Reset highlighted row
    this.setState({ scrollToIndex: undefined });
    // TODO: handle sorting of different values e.g. date, priority etc.
    let data = this.state.data.slice();
    if (this.sorters[sortBy]) {
      if (sortDirection === "ASC") {
        data.sort(this.sorters[sortBy]);
      } else {
        data = data.sort(this.sorters[sortBy]).reverse();
      }
    } else {
      data.sort(compareValues(sortBy) as (a: T, b: T) => number);
      if (sortDirection === "DESC") {
        data = data.reverse();
      }
    }

    this.setState({ data, sortBy: sortBy as keyof T, sortDirection });
  };

  filter = () => {
    const { data } = this.props;
    const { filters } = this.state;
    // Handle filtering from props
    if (typeof this.props.onFilter === "function") {
      this.props.onFilter(filters);
      return;
    }
    const filterKeys = Object.keys(filters);
    const filterValues = Object.values(filters);
    const filteredData = data.filter((item) => {
      let result: boolean = true;
      for (let idx = 0; idx < filterKeys.length; idx++) {
        const filterKey = filterKeys[idx];
        // if current key does not have filter values - it should not be removed from list
        if (!filterValues[idx] || !filterValues[idx].length) {
          result = true;
        }
        // if current key does not have predicate function - skip filtering
        if (typeof this.predicates[filterKey] === "function") {
          const predicate = this.predicates[filterKey];
          if (
            // if current item matches to one of filter values
            filterValues[idx].some((filterValue) => predicate(filterValue, item[filterKey])) &&
            /* and previous result was true => only then return "true"
            (used to handle multiple selected filters when one record should match all of the selected filters)
             */
            result === true
          ) {
            result = true;
          } else {
            result = false;
          }
        } else {
          result = true;
        }
      }
      return result;
    });

    this.setState({ data: filteredData });
    // Reset selected rows when use filter - the simplest approach :)
    this.setSelectedRows([]);
  };

  setFilter = (namespace: string, filters: FilterType) => {
    this.scrollToTop();
    this.setState((prevState) => {
      // when receiving an empty array - remove the namespace from state
      if (!filters?.length) {
        const copy = { ...prevState.filters };
        delete copy[namespace];
        return { ...prevState, filters: copy };
      }
      return { ...prevState, filters: { ...prevState.filters, [namespace]: filters } };
    }, this.filter);
  };

  scrollToTop() {
    const container = document.querySelector(".ReactVirtualized__Grid.ReactVirtualized__Table__Grid");
    container.scrollTop = 0;
  }

  public resetFilters = () => {
    this.setState({ filters: {} });
  };

  public setSortBy = (value: string) => {
    this.setState({ sortBy: value as keyof T });
  };

  public setSortDirection = (value: SortDirectionType) => {
    this.setState({ sortDirection: value });
  };

  rowRender: TableRowRenderer = (props) => {
    // Use-case - the total amount of files is loaded separately and we need to show that
    // loading is still in progress while showing the intermediate result at the same time
    if (isEmpty(props.rowData) || props.rowData === "LOADING") {
      return (
        <div className="loading-row" key={props.index} style={props.style}>
          <div className="placeholder-row" />
        </div>
      );
    }
    if (this.props.draggableRows) {
      return dndRowRenderer({
        onDragStart: this.onDragStart,
        normalizeDropRowData: this.props.normalizeDropRowData,
        getDraggableFromRowData: this.props.getDraggableFromRowData,
        getDroppableFromRowData: this.props.getDroppableFromRowData,
        ...props,
      });
    }
    return defaultTableRowRenderer(props);
  };

  onDragStart = (e: React.DragEvent) => {
    const rowData = e.currentTarget instanceof HTMLElement ? e.currentTarget.dataset.rowdata : "";
    e.dataTransfer.setData("text", rowData);
  };

  onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    let droppedTargetRow: HTMLDivElement;
    if ((e.target as HTMLDivElement).classList.contains("ReactVirtualized__Table__row")) {
      droppedTargetRow = e.target as HTMLDivElement;
    } else {
      droppedTargetRow = (e.target as HTMLDivElement).closest(".ReactVirtualized__Table__row");
      if (!droppedTargetRow) {
        // probably dropped on table header or smth
        return false;
      }
    }
    const droppedTargetData = droppedTargetRow instanceof HTMLElement ? droppedTargetRow.dataset.rowdata : "";
    const dataTransfer = e.dataTransfer.getData("text");
    if (dataTransfer) {
      const dragRowData = JSON.parse(dataTransfer);
      const dropRowData = JSON.parse(droppedTargetData);
      if (this.props.onDrop) {
        return this.props.onDrop({ dragRowData, dropRowData });
      }
    }
  };

  /**
   * Function is using by the "Views.tsx" component
   * to reset data of active filters (this.state.filters)
   * ? TODO: find better solution for managing filters outside
   */
  manageFilters = async (filters: ActiveFilters) => {
    await new Promise<void>((res) => {
      this.setState({ filters }, res);
    });
  };

  setSelectedRows = (selectedRows: T[]) => {
    const selectedRowKeys = selectedRows.map((row) => row?.[this.props.rowKey]);
    this.setState({ selectedRows, selectedRowKeys }, () => {
      if (typeof this.props.onSelectRows === "function") {
        this.props.onSelectRows({ selectedRows, selectedRowKeys });
      }
    });
  };

  handleSelectRow = (rowData: T, checked: boolean) => {
    const { rowKey } = this.props;
    const { selectedRows } = this.state;
    const rows = selectedRows.slice();
    if (checked) {
      rows.push(rowData);
    } else {
      const idx = rows.findIndex((item) => item[rowKey] === rowData[rowKey]);
      if (idx !== -1) {
        rows.splice(idx, 1);
      }
    }
    this.setSelectedRows(rows);
  };

  handleCheckAll = async () => {
    let selectedRows: T[];
    if (this.state.selectedRows.length === 0) {
      if (typeof this.props.onCheckAllPredicate === "function") {
        selectedRows = this.props.data.filter(this.props.onCheckAllPredicate);
      } else if (this.props.loadMore) {
        this.setState({ loadingMore: true });
        /**
         * If user scrolls with mouse to the end/middle of the table with lazy loading feature
         * we need to take care that the scrolled position has items injected at scroll position.
         * That's why for example in Views we pass to the data source the array filled with empty
         *  items and length of total amount of files within the table that comes from the BE.
         * In order get the correct offset (startIndex) parameter we need to check either the
         * first "undefined" item in the list, or the length of the fetched array depending what
         * matches earlier.
         */
        const firstUndefinedIndex = this.props.data.findIndex((item) => item === undefined);
        const startIndex = firstUndefinedIndex !== -1 ? firstUndefinedIndex : this.props.data.length;
        const stopIndex = this.props.rowCount;
        // split by multiple chunks if data length is more that batch size
        const diff = stopIndex - startIndex;
        if (diff > BATCH_SIZE || this.props.alwaysCallSelectAll) {
          if (this.props.selectAllRequest) {
            selectedRows = (await this.props.selectAllRequest()) as T[];
          } else {
            if (diff / BATCH_SIZE > 10) {
              // when the total records size is very large (more than 10 stacks)
              // and the custom "select all" request handler is available - use it
              // otherwise set the maximum stack size to 10.

              const stacks = 10;
              const customBatchSize = Math.ceil(diff / 10);
              await Promise.all(
                Array(stacks)
                  .fill(undefined)
                  .map((_, idx) => {
                    const start = startIndex + idx * customBatchSize;
                    const end = start + customBatchSize - 1;
                    return this.handleLoadMore({ startIndex: start, stopIndex: end });
                  })
              );
            } else {
              const stacks = Math.ceil(diff / BATCH_SIZE);
              await Promise.all(
                Array(stacks)
                  .fill(undefined)
                  .map((_, idx) => {
                    const start = startIndex + idx * BATCH_SIZE;
                    const end = start + BATCH_SIZE - 1;
                    return this.handleLoadMore({ startIndex: start, stopIndex: end });
                  })
              );
            }
            // a little delay to wait for props.data to have all fetched records.
            await waitFor(400);
            selectedRows = this.props.data.slice();
          }
        } else {
          await this.handleLoadMore({ startIndex: startIndex, stopIndex: stopIndex });
          // a little delay to wait for props.data to have all fetched records.
          await waitFor(400);
          selectedRows = this.props.data.slice();
        }
        this.setState({ loadingMore: false });
      } else {
        selectedRows = this.props.data.slice();
      }
      if (!selectedRows) {
        selectedRows = this.props.data.slice();
      }
    } else {
      selectedRows = [];
    }
    this.setSelectedRows(selectedRows);
  };

  checkboxProps = (rowData: T) => {
    if (typeof this.props.getCheckboxProps === "function") {
      return this.props.getCheckboxProps(rowData);
    } else {
      return {};
    }
  };

  /**
   * Minimum pixel width when resizing (react-virtualized `minWidth` per column, else
   * `resizeMinWidth`, else default).
   */
  getMinResizeWidthForColumn(columnIndex: number): number {
    const floor = this.props.resizeMinWidth ?? DEFAULT_COLUMN_RESIZE_MIN_WIDTH;
    const cols = this.props.columns || [];
    if (typeof this.props.onSelectRows === "function") {
      if (columnIndex === 0) {
        return 24;
      }
      const propCol = cols[columnIndex - 1];
      return propCol.minWidth ?? floor;
    }
    const propCol = cols[columnIndex];
    return propCol?.minWidth ?? floor;
  }

  handleLoadMore = ({ startIndex, stopIndex }: IndexRange) => {
    if (this.startIndex !== startIndex || this.stopIndex !== stopIndex) {
      this.startIndex = startIndex;
      this.stopIndex = stopIndex;
      return this.props.loadMore({ limit: stopIndex - startIndex + 1, offset: startIndex });
    }
    return Promise.resolve();
  };

  get isIntermediate() {
    return this.state.selectedRows.length && this.state.selectedRows.length !== this.state.data.length;
  }

  get isAllChecked() {
    return this.state.selectedRows.length && this.state.selectedRows.length === this.state.data.length;
  }

  get columns() {
    let columns = this.props.columns || [];
    const { onSelectRows } = this.props;
    columns = columns.slice();
    // if "onSelectRows" callback is set - add row selection checkboxes into the table
    if (typeof onSelectRows === "function") {
      columns.unshift({
        dataKey: this.props.rowKey,
        width: 30,
        flexShrink: 0,
        headerClassName: "no-border",
        disableSort: true,
        cellRenderer: ({ cellData, rowData }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={(this.state.selectedRowKeys as unknown as string[]).includes(cellData)}
              onChange={(e) => this.handleSelectRow(rowData, e.target.checked)}
              {...this.checkboxProps(rowData)}
            />
          </div>
        ),
        label: (
          <Checkbox indeterminate={this.isIntermediate} checked={this.isAllChecked} onChange={this.handleCheckAll} />
        ),
      });
    }
    columns = columns.map((item, index) => ({
      ...item,
      flexGrow: this.state.adjustedColumnWidthes[index] ? undefined : item.flexGrow,
      // to hide border-right for header without label
      headerClassName: classnames(item.headerClassName, { invisible: !item.label }),
      // enhance column props with custom header
      headerRenderer: ({ dataKey, disableSort, label, sortBy, sortDirection }) => (
        <ScrollableTableHeader
          disableSort={disableSort}
          dataKey={dataKey}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label={label as any}
          sortBy={sortBy}
          sortDirection={sortDirection}
          filters={item.filters}
          selectedFilters={this.state.filters[dataKey]}
          onFilter={(filters: FilterType) => this.setFilter(dataKey, filters)}
          filterDropdownMinWidth={this.props.filterDropdownMinWidth}
          customFilterRender={item.customFilterRender}
          singleFilter={item.singleFilter}
          headerDataTestId={item.headerDataTestId}
          filterIconDataTestId={item.filterIconDataTestId}
          onResize={this.props.resizeableHeaders ? this.handleResize : undefined}
          handleDoubleClickResize={this.handleDoubleClickResize}
          width={this.state.adjustedColumnWidthes[index] || item.width}
          index={index}
          ignoreResizeableHeaders={this.props.ignoreResizeableHeaders}
          minResizeWidth={this.getMinResizeWidthForColumn(index)}
        />
      ),
    }));
    return columns;
  }

  handleResize = (columnIndex: number, width: number) => {
    const min = this.getMinResizeWidthForColumn(columnIndex);
    const w = Math.max(min, width);
    const adjustedColumnWidthes = { ...this.state.adjustedColumnWidthes };
    adjustedColumnWidthes[columnIndex] = w;
    this.setState({ adjustedColumnWidthes });
    this.saveWidthesToLocalStorageDebounce(adjustedColumnWidthes);
  };

  getStringType = (node) => {
    if (typeof node.type === "string") return node.type;
    if (node.type && node.type.displayName) return node.type.displayName;
    return "";
  };

  getLinkCaption(node) {
    return node.children ? node.children.props.children : node.props.children;
  }

  handleDoubleClickResize = (e: React.MouseEvent, columnIndex: number) => {
    e.stopPropagation();

    try {
      const column = this.props.columns[typeof this.props.onSelectRows === "function" ? columnIndex - 1 : columnIndex];
      let maxWidth = 0;
      const container = document.createElement("span");
      container.style.whiteSpace = "nowrap";
      container.style.position = "absolute";
      document.body.appendChild(container);

      // getting max width of the header column
      const headerColumn = (e.target as HTMLElement).parentElement.cloneNode(true) as HTMLElement;
      headerColumn.style.display = "flex";
      container.appendChild(headerColumn);
      maxWidth = Math.max(maxWidth, headerColumn.scrollWidth + 10);

      // getting max width of the body column's (also the not rendered ones are considered)
      this.props.data.forEach((rowData, rowIndex) => {
        const cellData = rowData[column.dataKey];
        if (column.cellRenderer) {
          const reactnode = column.cellRenderer({
            cellData,
            rowData,
            columnIndex,
            rowIndex,
            dataKey: column.dataKey as string,
            isScrolling: false,
          });

          const renderedElement = ReactDOM.createPortal(reactnode, container);
          if (this.getStringType(reactnode) === "Link") {
            // needed, cause type <Link> otherwise rendering isn't working
            container.innerText = this.getLinkCaption(renderedElement);
          } else {
            ReactDOM.render(renderedElement, container);
          }
        } else if (column.cellDataGetter) {
          const reactnode = column.cellDataGetter({
            columnData: cellData,
            rowData,
            dataKey: column.dataKey as string,
          });
          const renderedElement = ReactDOM.createPortal(reactnode, container);
          ReactDOM.render(renderedElement, container);
        } else if (typeof cellData === "string" || typeof cellData === "number") {
          container.innerText = "" + cellData;
        } else {
          // unknow case, return next
          return;
        }
        maxWidth = Math.max(maxWidth, container.scrollWidth + 10);
      });

      document.body.removeChild(container);
      this.handleResize(columnIndex, maxWidth);
    } catch (error) {
      console.error(error);
    }
  };

  private buildAdjustedColumnWidthsFromStorage(widthesbyDataKey: Record<string, unknown>): {
    [columnIndex: number]: number;
  } {
    const adjustedColumnWidthes: { [columnIndex: number]: number } = {};
    const hasCheckbox = typeof this.props.onSelectRows === "function";
    for (const dataKey in widthesbyDataKey) {
      const idx = this.props.columns.findIndex((col) => col.dataKey === dataKey);
      if (idx === -1) {
        continue;
      }
      const storageIdx = hasCheckbox ? idx + 1 : idx;
      const raw = widthesbyDataKey[dataKey];
      const min = this.getMinResizeWidthForColumn(storageIdx);
      const width = typeof raw === "number" ? raw : min;
      adjustedColumnWidthes[storageIdx] = Math.max(min, width);
    }
    return adjustedColumnWidthes;
  }

  readSavedWidtesFromLocalStorage() {
    if (typeof this.props.resizeableHeaders !== "string" || !window.localStorage) {
      return;
    }
    try {
      const widthesbyDataKey = JSON.parse(window.localStorage.getItem(this.props.resizeableHeaders)) as Record<
        string,
        unknown
      >;
      const adjustedColumnWidthes = this.buildAdjustedColumnWidthsFromStorage(widthesbyDataKey);
      if (Object.keys(adjustedColumnWidthes).length > 0) {
        this.setState({ adjustedColumnWidthes });
      }
    } catch (e) {
      notificationError(
        "Error",
        i18n.t("Something technical went wrong while restoring the saved column widthes", {
          ns: I18N_NS.GENERAL,
        })
      );
      console.error(e);
    }
  }

  saveWidthesToLocalStorage(adjustedColumnWidthes: { [columnIndex: number]: number }) {
    if (typeof this.props.resizeableHeaders === "string" && window.localStorage) {
      const adjustedColumnWidthesByDataKey: { [dataKey: string]: number } = {};
      const hasCheckbox = typeof this.props.onSelectRows === "function";
      const cols = this.props.columns || [];
      for (const idx in adjustedColumnWidthes) {
        const numIdx = Number(idx);
        if (hasCheckbox && numIdx === 0) continue;
        const propsIdx = hasCheckbox ? numIdx - 1 : numIdx;
        const col = cols[propsIdx];
        if (col?.dataKey != null) {
          adjustedColumnWidthesByDataKey[String(col.dataKey)] = adjustedColumnWidthes[idx];
        }
      }
      window.localStorage.setItem(this.props.resizeableHeaders, JSON.stringify(adjustedColumnWidthesByDataKey));
    }
  }

  get sortBy() {
    return this.props.sortBy || this.state.sortBy;
  }

  get sortDirection() {
    return this.props.sortDirection || this.state.sortDirection;
  }

  get data() {
    if (this.props.onFilter || this.props.onSort || this.props.useDataFromProps) {
      return this.props.data;
    }
    return this.state.data;
  }

  getRowClassName = ({ index }: Index) => {
    let extendedRowClName: string;
    let highlightedbyIndex: boolean;
    if (this.props.highlightedRowId) {
      highlightedbyIndex =
        index !== -1
          ? (this.props.highlightedRowId as Array<unknown>).includes(this.props.data[index]?.[this.props.rowKey])
          : false;
    }
    if (index !== -1 && this.props.rowClassName) {
      if (typeof this.props.rowClassName === "function") {
        extendedRowClName = this.props.rowClassName(this.data[index]);
      } else {
        extendedRowClName = this.props.rowClassName;
      }
    }
    return classnames("scrollable-table-row", extendedRowClName, {
      clickable: this.props.onRowClick,
      highlighted: highlightedbyIndex,
      "sm-font-size": this.props.smallFontSize,
    });
  };

  renderTable(onRowsRendered) {
    const { scrollToIndex } = this.state;
    const { stretchHeight, loading, rowCount, virtualizedTableProps, wrapperStyles, rowHeight, onColumnReorder } =
      this.props;
    const columnDataKeys = this.columns.map((c) => c.dataKey as string);
    const tableProps = {
      ...virtualizedTableProps,
      ...(onColumnReorder && {
        headerRowRenderer: createSortableHeaderRowRenderer(columnDataKeys, onColumnReorder),
      }),
    };
    return (
      <div style={{ height: "100%", width: "100%", display: "flex", ...wrapperStyles }} onDrop={this.onDrop}>
        <TableCore
          stretchHeight={stretchHeight}
          loading={loading || this.state.loadingMore}
          ref={this.tableRef}
          rowClassName={this.getRowClassName}
          sort={this.sort}
          onRowsRendered={onRowsRendered}
          sortBy={this.sortBy as string}
          sortDirection={this.sortDirection}
          scrollToIndex={scrollToIndex}
          rowCount={rowCount || (this.data && this.data.length) || 0}
          rowHeight={rowHeight}
          rowGetter={({ index }) => this.data[index] || {}}
          onRowClick={this.props.onRowClick}
          rowRenderer={this.rowRender}
          {...tableProps}
        >
          {this.columns.map(
            ({ width: colWidth, dataKey, headerDataTestId: _htid, filterIconDataTestId: _fitid, ...colProps }, idx) => (
              <Column
                key={idx}
                dataKey={dataKey as string}
                {...colProps}
                width={this.state.adjustedColumnWidthes[idx] || colWidth}
              />
            )
          )}
        </TableCore>
      </div>
    );
  }

  render() {
    const { rowCount, loadMore } = this.props;
    if (loadMore !== undefined) {
      return (
        <InfiniteLoader
          isRowLoaded={({ index }) => !!this.data[index]}
          loadMoreRows={this.debounceHandleLoadMore}
          rowCount={rowCount || (this.data && this.data.length) || 0}
          minimumBatchSize={BATCH_SIZE}
          threshold={50}
        >
          {({ onRowsRendered }) => {
            return this.renderTable(onRowsRendered);
          }}
        </InfiniteLoader>
      );
    } else {
      return this.renderTable(undefined);
    }
  }
}
